import routes from '@msteams/Routes/routes'
import { SINGLE_LINKS } from '@msteams/components/Navigation/utils'
import { filterNavItems } from '@src/components/Navigation/utils'
import type { NavFilterUser } from '@src/components/Navigation/utils'

/**
 * The two Teams routers register different page sets. The personal app has
 * every top-nav destination; the channel tab (teamRouter) only renders the
 * team overview (at the approval-requests path) and the assistant. A
 * landing must never name a path its router does not register, or the
 * redirect lands on nothing.
 */
export type TeamsScope = 'personal' | 'team'

const TEAM_TAB_PAGES = new Set<string>([routes.private.approvalRequests, routes.private.assistant])

/**
 * Where the Teams app lands a user (the index / `/tab` / `/dashboard`
 * redirects in both routers): the first entry of the Teams top nav they
 * may USE, in display order, among the pages the router registers. The
 * same resolved feature list the nav reads, so a role that cannot open
 * Approval Requests is not parked on a page that 403s — which is what the
 * hard-coded redirect did.
 *
 * Same pick order as the web shell's getLandingRoute: real work surfaces
 * first, then the AI Assistant only as a fallback (it sits early in the
 * nav and would otherwise win over Tasks or Workflows), then the router's
 * last resort — personal settings in the app (ungated on both shells), the
 * team overview in the channel tab (its page handles a 403 itself). Plan-
 * locked and hidden entries are not candidates: a paywall is not a work
 * surface.
 */
export function getTeamsLandingRoute(user: NavFilterUser | undefined, scope: TeamsScope = 'personal'): string {
  const lastResort = scope === 'team' ? routes.private.approvalRequests : routes.private.settings
  if (!user) return lastResort
  const usable = filterNavItems(SINGLE_LINKS, user).filter(
    (link) => scope === 'personal' || TEAM_TAB_PAGES.has(link.to),
  )
  const notPrimary = new Set<string>([routes.private.settings, routes.private.assistant])
  const first = usable.find((link) => !notPrimary.has(link.to))
  if (first) return first.to
  return usable.some((link) => link.to === routes.private.assistant) ? routes.private.assistant : lastResort
}
